import { Box, Divider, Stack, Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { FcGridInfo } from "~/components/serviceSupport/FcGridInfo";
import { DangerZoneWrapper } from "~/components/settings/DangerZoneWrapper";
import {
  SettingsSectionWrapper,
  SettingsSubsection
} from "~/components/settings/SettingsSection";
import WarehouseResponseChart from "~/features/settings/WarehouseResponseChart";
import { OverviewFieldRow } from "~/features/settings/components/OverviewFieldRow";
import {
  settingsOverviewFormControlMaxWidthPx,
  settingsPagePaddingSx,
  settingsSectionGap
} from "~/features/settings/settings.styles";
import {
  selectDropdownWorkstationId,
  selectSelectedGridId,
  setDropdownWorkstationId,
  setSelectedGridId
} from "~/features/workstationConfig/workstationConfig.slice";
import { useNavbar, ViewNameTranslation } from "~/hooks/useNavbar";
import { determinePortId } from "~/lib/helpers";
import { selectUserIsAdmin } from "~/redux/selectors/authSelectors";
import { selectUsersFulfillmentCenter } from "~/redux/selectors/storeSelectors";
import {
  selectDropdownWorkstation,
  selectIsDropdownWorkstationPickingRoleEnabled
} from "~/redux/selectors/workstationConfigSelectors";
import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";
import { useGetAutostoreGridsQuery } from "~/redux/warehouse/autostoreGrid.hooks";
import { useGetWorkstationsQuery } from "~/redux/warehouse/workstation.hooks";
import { WorkstationSummaryDto } from "~/types/api";

import { AddWorkstation } from "./components/AddWorkstation";

import { OrderTypeSelect } from "./components/OrderTypeSelect";
import { PortSetup } from "./components/PortSetup";
import { PrinterSettings } from "./components/PrinterSettings";
import { SiteWorkstationAssignment } from "./components/SiteWorkstationAssignment";
import { TempZoneSelect } from "./components/TempZoneSelect";
import { TotePlacementManagement } from "./components/TotePlacementManagement";
import { WorkstationDangerZone } from "./components/WorkstationDangerZone";
import { WorkstationOrientationSelect } from "./components/WorkstationOrientationSelect";
import { WorkstationRolesSelect } from "./components/WorkstationRolesSelect";
import { useWorkstationConfigForm } from "./hooks/useWorkstationConfigForm";
import {
  isWorkstationInList,
  sortWorkstationsByGrid
} from "./settingsWorkstation.logic";
import { overviewOverviewFieldsTwoColumnBp } from "./workstation.styles";

type Props = {
  viewTitle?: ViewNameTranslation;
};

export function WorkstationSettingsView({ viewTitle }: Props) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  useNavbar({ viewTitle });

  const siteWorkstation = useAppSelector(selectThisWorkstation);
  const userFc = useAppSelector(selectUsersFulfillmentCenter);
  const showPrinterSection = userFc?.printerConfiguration === "BrowserPrint";
  const isAdmin = useAppSelector(selectUserIsAdmin);
  const workstation = useAppSelector(selectDropdownWorkstation);
  const isPickingRoleEnabled = useAppSelector(
    selectIsDropdownWorkstationPickingRoleEnabled
  );
  const { data: fetchedWorkstations } = useGetWorkstationsQuery();
  const { data: autostoreGrids } = useGetAutostoreGridsQuery(undefined, {
    skip: !userFc
  });
  const dropdownWorkstationId = useAppSelector(selectDropdownWorkstationId);
  const selectedGridId = useAppSelector(selectSelectedGridId);
  const dropdownBootstrapDoneRef = useRef(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const handleExitNameEdit = useCallback(() => {
    setIsEditingName(false);
  }, []);

  const workstations = useMemo(
    () =>
      fetchedWorkstations
        ? sortWorkstationsByGrid(fetchedWorkstations, selectedGridId)
        : undefined,
    [fetchedWorkstations, selectedGridId]
  );

  const form = useWorkstationConfigForm({
    viewTitle,
    onExitNameEdit: handleExitNameEdit,
    isEditingName
  });
  const {
    formState: { isDirty, errors },
    control,
    pickingTempZoneOptions,
    canEditPickingTempZones
  } = form;
  const isPickingTempZonesDisabled =
    !isAdmin || !isPickingRoleEnabled || !canEditPickingTempZones;
  const pickingTempZonesRequiredError =
    errors.temperatureZones?.type === "required";

  // Keep selectedGridId aligned with grids for the current FC
  useEffect(() => {
    const fulfillmentCenterId = userFc?.fulfillmentCenterId;
    if (!fulfillmentCenterId) {
      dispatch(setSelectedGridId(undefined));
      return;
    }
    if (!autostoreGrids) {
      return;
    }

    const wsGridId = siteWorkstation?.autostoreGridId;
    dispatch((_dispatch, getState) => {
      const previousGridId = selectSelectedGridId(getState());
      if (
        previousGridId &&
        autostoreGrids.some((g) => g.autostoreGridId === previousGridId)
      ) {
        return;
      }

      const nextGridId =
        wsGridId && autostoreGrids.some((g) => g.autostoreGridId === wsGridId)
          ? wsGridId
          : undefined;

      if (nextGridId !== previousGridId) {
        _dispatch(setSelectedGridId(nextGridId));
      }
    });
  }, [
    autostoreGrids,
    dispatch,
    userFc?.fulfillmentCenterId,
    siteWorkstation?.autostoreGridId
  ]);

  // If selectedGridId is corrected after bootstrap ran against an empty
  // filtered list (stale persisted grid), allow overview to reselect
  useEffect(() => {
    dropdownBootstrapDoneRef.current = false;
  }, [selectedGridId]);

  useEffect(() => {
    if (workstations === undefined) {
      return;
    }

    const siteId = siteWorkstation?.id;
    const preferredId =
      siteId && isWorkstationInList(workstations, siteId) ? siteId : "";

    if (!dropdownBootstrapDoneRef.current) {
      dropdownBootstrapDoneRef.current = true;
      if (dropdownWorkstationId !== preferredId) {
        dispatch(setDropdownWorkstationId(preferredId));
      }
      return;
    }

    if (
      dropdownWorkstationId &&
      !isWorkstationInList(workstations, dropdownWorkstationId)
    ) {
      dispatch(setDropdownWorkstationId(preferredId));
    }
  }, [dispatch, workstations, dropdownWorkstationId, siteWorkstation?.id]);

  useEffect(() => {
    const updateWorkstation = () => {
      const updatedThisWorkstation = fetchedWorkstations?.find(
        (ws: WorkstationSummaryDto) => ws.id === siteWorkstation?.id
      );
      const shouldSyncFromServer =
        updatedThisWorkstation &&
        siteWorkstation &&
        updatedThisWorkstation.id === siteWorkstation.id &&
        updatedThisWorkstation.mode !== siteWorkstation.mode;

      if (shouldSyncFromServer) {
        dispatch({
          type: "workstations/SET_THIS_WORKSTATION",
          payload: updatedThisWorkstation
        });
        const portId = determinePortId(updatedThisWorkstation);
        dispatch({ type: "workstations/SET_SITE_PORT_ID", payload: portId });
      }
    };
    updateWorkstation();
  }, [dispatch, fetchedWorkstations, siteWorkstation]);

  return (
    <Box id="ws-settings-page-root" sx={settingsPagePaddingSx}>
      <Stack id="ws-settings-page-stack" spacing={settingsSectionGap}>
        <Stack
          id="ws-settings-page-title-row"
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap={{ xs: "wrap", md: "nowrap" }}
          gap={1.5}
          sx={{
            minWidth: 0,
            columnGap: 2
          }}
        >
          <Typography
            variant="h5"
            component="h1"
            sx={{
              flex: "0 1 auto",
              minWidth: 0,
              fontWeight: 500,
              letterSpacing: 0
            }}
          >
            {t("workstations")}
          </Typography>
          <Box
            sx={{
              flex: "1 1 auto",
              minWidth: 0,
              display: "flex",
              justifyContent: { xs: "flex-start", md: "flex-end" }
            }}
          >
            <FcGridInfo compact />
          </Box>
        </Stack>

        <FormProvider {...form}>
          <form id="ws-settings-form" autoComplete="off">
            <Stack id="ws-settings-form-stack" spacing={settingsSectionGap}>
              <SettingsSectionWrapper
                id="ws-settings-overview-section"
                title={t("overview")}
              >
                <Box
                  id="ws-settings-overview-grid"
                  display="grid"
                  gridTemplateColumns={{
                    xs: "1fr",
                    [overviewOverviewFieldsTwoColumnBp]:
                      "repeat(2, minmax(0, 1fr))"
                  }}
                  columnGap={{
                    xs: 0,
                    [overviewOverviewFieldsTwoColumnBp]: 2.5
                  }}
                  rowGap={1.5}
                  sx={{ alignItems: "start", maxWidth: "100%" }}
                >
                  <Box
                    id="ws-settings-overview-assign-cell"
                    sx={{
                      gridColumn: {
                        xs: 1,
                        [overviewOverviewFieldsTwoColumnBp]: "1 / -1"
                      },
                      gridRow: 1,
                      minWidth: 0,
                      width: "100%"
                    }}
                  >
                    <SiteWorkstationAssignment
                      isEditingName={isEditingName}
                      setIsEditingName={setIsEditingName}
                    />
                  </Box>
                  {workstation && (
                    <>
                      <Box
                        id="ws-settings-overview-roles-cell"
                        sx={{
                          gridColumn: 1,
                          gridRow: 2,
                          minWidth: 0,
                          width: "100%"
                        }}
                      >
                        <OverviewFieldRow
                          label={t("workstation roles label")}
                          helperText={t("workstation roles overview helper")}
                        >
                          <Box
                            sx={{
                              width: "100%",
                              maxWidth: settingsOverviewFormControlMaxWidthPx,
                              minWidth: 0
                            }}
                          >
                            <WorkstationRolesSelect />
                          </Box>
                        </OverviewFieldRow>
                      </Box>
                      {isPickingRoleEnabled ? (
                        <Box
                          id="ws-settings-overview-order-types-cell"
                          sx={{
                            gridColumn: {
                              xs: 1,
                              [overviewOverviewFieldsTwoColumnBp]: 2
                            },
                            gridRow: {
                              xs: 3,
                              [overviewOverviewFieldsTwoColumnBp]: 2
                            },
                            minWidth: 0,
                            width: "100%"
                          }}
                        >
                          <OverviewFieldRow
                            label={t("order types")}
                            helperText={t(
                              "workstation order types overview helper"
                            )}
                          >
                            <Box
                              sx={{
                                width: "100%",
                                maxWidth: settingsOverviewFormControlMaxWidthPx,
                                minWidth: 0
                              }}
                            >
                              <OrderTypeSelect
                                control={control}
                                fieldName="pickableOrderTypes"
                              />
                            </Box>
                          </OverviewFieldRow>
                        </Box>
                      ) : null}
                      <Box
                        id="ws-settings-overview-orientation-cell"
                        sx={{
                          gridColumn: {
                            xs: 1,
                            [overviewOverviewFieldsTwoColumnBp]: 1
                          },
                          gridRow: {
                            xs: isPickingRoleEnabled ? 4 : 3,
                            [overviewOverviewFieldsTwoColumnBp]: 3
                          },
                          minWidth: 0,
                          width: "100%"
                        }}
                      >
                        <OverviewFieldRow
                          label={t("workstation orientation")}
                          helperText={t(
                            "workstation orientation overview helper"
                          )}
                        >
                          <Box
                            sx={{
                              width: "100%",
                              maxWidth: settingsOverviewFormControlMaxWidthPx,
                              minWidth: 0
                            }}
                          >
                            <WorkstationOrientationSelect />
                          </Box>
                        </OverviewFieldRow>
                      </Box>
                      <Box
                        id="ws-settings-overview-temp-zones-cell"
                        sx={{
                          gridColumn: {
                            xs: 1,
                            [overviewOverviewFieldsTwoColumnBp]: 2
                          },
                          gridRow: {
                            xs: isPickingRoleEnabled ? 5 : 4,
                            [overviewOverviewFieldsTwoColumnBp]: 3
                          },
                          minWidth: 0,
                          width: "100%"
                        }}
                      >
                        <OverviewFieldRow
                          label={t("picking temp zones")}
                          helperText={
                            pickingTempZonesRequiredError
                              ? t("at least one picking temp zone is required")
                              : t(
                                  "workstation picking temp zones overview helper"
                                )
                          }
                          helperTextError={pickingTempZonesRequiredError}
                          helperTextMinLines={2}
                        >
                          <Box
                            sx={{
                              width: "100%",
                              maxWidth: settingsOverviewFormControlMaxWidthPx,
                              minWidth: 0
                            }}
                          >
                            <TempZoneSelect
                              control={control}
                              fieldName="temperatureZones"
                              options={pickingTempZoneOptions}
                              disabled={isPickingTempZonesDisabled}
                            />
                          </Box>
                        </OverviewFieldRow>
                      </Box>
                    </>
                  )}
                </Box>
              </SettingsSectionWrapper>

              {workstation && <Divider role="presentation" />}

              {workstation && (
                <SettingsSectionWrapper
                  id="ws-settings-ports-section"
                  title={t("ports")}
                >
                  <PortSetup />
                </SettingsSectionWrapper>
              )}

              {workstation && <Divider role="presentation" />}

              {workstation && (
                <SettingsSectionWrapper
                  id="ws-settings-devices-section"
                  title={t("devices section title")}
                  gap={2}
                >
                  <Stack spacing={2}>
                    <SettingsSubsection
                      id="ws-settings-ptl-subsection"
                      title={t("put to light subsection")}
                    >
                      <TotePlacementManagement />
                    </SettingsSubsection>
                    {showPrinterSection ? (
                      <>
                        <Divider role="presentation" />
                        <SettingsSubsection
                          id="ws-settings-printer-subsection"
                          title={t("printer subsection")}
                          gap={2}
                        >
                          <PrinterSettings maxWidth="sm" />
                        </SettingsSubsection>
                      </>
                    ) : null}
                  </Stack>
                </SettingsSectionWrapper>
              )}

              {workstation && <Divider role="presentation" />}

              {workstation &&
                siteWorkstation &&
                workstation.id === siteWorkstation.id && (
                  <SettingsSectionWrapper
                    id="ws-settings-connectivity-section"
                    title={t("connectivity test section title")}
                    gap={2}
                  >
                    <WarehouseResponseChart />
                  </SettingsSectionWrapper>
                )}
            </Stack>
          </form>
        </FormProvider>

        {!isDirty && !isEditingName && isAdmin && (
          <Stack id="ws-settings-create-workstation-block" spacing={2}>
            <Divider role="presentation" />
            <SettingsSectionWrapper title={t("create workstation")} gap={2}>
              <AddWorkstation />
            </SettingsSectionWrapper>
          </Stack>
        )}

        {isAdmin && workstation && (
          <Stack
            id="ws-settings-danger-zone-block"
            spacing={settingsSectionGap}
          >
            <Divider role="presentation" />
            <DangerZoneWrapper title={t("danger zone section title")}>
              <WorkstationDangerZone />
            </DangerZoneWrapper>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
